// Terms of Service / CGU - drbooking
// Conditions Générales d'Utilisation

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowLeft, FileText, Scale, Shield, AlertTriangle } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();
  const lastUpdated = "28 Mars 2026";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-800">
      {/* Header */}
      <div className="bg-blue-600 text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <Button
            variant="ghost"
            className="text-white hover:bg-blue-700 mb-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div className="flex items-center gap-3">
            <FileText className="h-10 w-10" />
            <div>
              <h1 className="text-3xl font-bold">Conditions Générales d'Utilisation</h1>
              <p className="text-blue-100">Dernière mise à jour : {lastUpdated}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-slate dark:prose-invert max-w-none">
          
          {/* Introduction */}
          <section className="mb-8">
            <h2 className="flex items-center gap-2 text-2xl font-bold text-slate-900 dark:text-white">
              <Scale className="h-6 w-6 text-blue-600" />
              1. Objet
            </h2>
            <p className="text-slate-600 dark:text-slate-300">
              Les présentes Conditions Générales d'Utilisation (CGU) régissent l'utilisation de la plateforme 
              drbooking (ci-après "la Plateforme"), éditée par drbooking, permettant la prise de rendez-vous 
              médicaux en ligne et la téléconsultation.
            </p>
            <p className="text-slate-600 dark:text-slate-300">
              L'utilisation de la Plateforme implique l'acceptation pleine et entière des présentes CGU.
            </p>
          </section>

          {/* Definitions */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">2. Définitions</h2>
            <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2">
              <li><strong>Utilisateur</strong> : Toute personne physique utilisant la Plateforme</li>
              <li><strong>Patient</strong> : Utilisateur recherchant ou prenant un rendez-vous médical</li>
              <li><strong>Professionnel de santé</strong> : Médecin, praticien ou établissement de santé inscrit sur la Plateforme</li>
              <li><strong>Compte</strong> : Espace personnel créé par l'Utilisateur</li>
              <li><strong>Rendez-vous</strong> : Créneau de consultation réservé via la Plateforme</li>
            </ul>
          </section>

          {/* Services */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">3. Services proposés</h2>
            <p className="text-slate-600 dark:text-slate-300">La Plateforme propose les services suivants :</p>
            <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2">
              <li>Recherche de professionnels de santé par spécialité et localisation</li>
              <li>Prise de rendez-vous en ligne</li>
              <li>Téléconsultation vidéo</li>
              <li>Rappels de rendez-vous par SMS et email</li>
              <li>Gestion de dossier patient</li>
              <li>Assistant vocal pour la prise de rendez-vous</li>
            </ul>
          </section>

          {/* Account */}
          <section className="mb-8">
            <h2 className="flex items-center gap-2 text-2xl font-bold text-slate-900 dark:text-white">
              <Shield className="h-6 w-6 text-blue-600" />
              4. Inscription et Compte
            </h2>
            <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mt-4">4.1 Création de compte</h3>
            <p className="text-slate-600 dark:text-slate-300">
              L'utilisation des services nécessite la création d'un compte. L'Utilisateur s'engage à fournir 
              des informations exactes et à les maintenir à jour.
            </p>
            
            <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mt-4">4.2 Sécurité du compte</h3>
            <p className="text-slate-600 dark:text-slate-300">
              L'Utilisateur est responsable de la confidentialité de ses identifiants. Les professionnels de santé 
              doivent obligatoirement activer l'authentification à deux facteurs (2FA).
            </p>
            
            <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mt-4">4.3 Suppression de compte</h3>
            <p className="text-slate-600 dark:text-slate-300">
              L'Utilisateur peut demander la suppression de son compte à tout moment. Cette suppression entraîne 
              l'effacement des données personnelles conformément au RGPD.
            </p>
          </section>

          {/* Obligations */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">5. Obligations des Utilisateurs</h2>
            <p className="text-slate-600 dark:text-slate-300">L'Utilisateur s'engage à :</p>
            <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2">
              <li>Utiliser la Plateforme conformément à sa destination</li>
              <li>Ne pas usurper l'identité d'un tiers</li>
              <li>Respecter les rendez-vous pris ou les annuler dans un délai raisonnable</li>
              <li>Ne pas utiliser la Plateforme à des fins frauduleuses</li>
              <li>Respecter les professionnels de santé et autres utilisateurs</li>
            </ul>
          </section>

          {/* Healthcare Professionals */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">6. Professionnels de santé</h2>
            <p className="text-slate-600 dark:text-slate-300">
              Les professionnels de santé inscrits sur la Plateforme :
            </p>
            <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2">
              <li>Garantissent être dûment autorisés à exercer leur profession</li>
              <li>S'engagent à maintenir leurs informations à jour</li>
              <li>Doivent signer un contrat de partenariat pour bénéficier de toutes les fonctionnalités</li>
              <li>Restent seuls responsables de leurs actes médicaux</li>
            </ul>
          </section>

          {/* TELECONSULTATION - NEW SECTION */}
          <section className="mb-8 p-6 border-2 border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 rounded-xl">
            <h2 className="flex items-center gap-2 text-2xl font-bold text-slate-900 dark:text-white">
              <AlertTriangle className="h-6 w-6 text-amber-500" />
              7. Téléconsultation - Conditions Spécifiques
            </h2>
            
            <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mt-4">7.1 Rôle de la Plateforme</h3>
            <p className="text-slate-600 dark:text-slate-300">
              drbooking est une plateforme de <strong>mise en relation</strong> entre patients et professionnels de santé. 
              La Plateforme :
            </p>
            <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2">
              <li><strong>N'EST PAS</strong> un établissement de santé</li>
              <li><strong>N'EXERCE PAS</strong> la médecine</li>
              <li><strong>N'EST PAS</strong> responsable des actes médicaux effectués lors des téléconsultations</li>
              <li><strong>VÉRIFIE</strong> l'inscription à l'Ordre des Médecins du pays d'exercice</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mt-4">7.2 Responsabilité du Professionnel</h3>
            <p className="text-slate-600 dark:text-slate-300">
              Le professionnel de santé pratiquant la téléconsultation via la Plateforme :
            </p>
            <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2">
              <li>Reste <strong>seul responsable</strong> de la qualité de ses actes médicaux</li>
              <li>Doit respecter la <strong>déontologie médicale</strong> et les règles de son Ordre</li>
              <li>Doit vérifier que la <strong>téléconsultation est légalement autorisée</strong> dans son pays d'exercice</li>
              <li>Doit disposer d'une <strong>assurance responsabilité civile professionnelle</strong> couvrant la télémédecine</li>
              <li>Est responsable de la <strong>conservation des données médicales</strong> selon la réglementation en vigueur</li>
              <li>Ne peut en aucun cas engager la responsabilité de drbooking pour ses actes</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mt-4">7.3 Responsabilité du Patient</h3>
            <p className="text-slate-600 dark:text-slate-300">
              Le patient utilisant la téléconsultation :
            </p>
            <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2">
              <li>Fournit des informations <strong>exactes et complètes</strong> sur son état de santé</li>
              <li>Ne peut pas utiliser la téléconsultation en cas d'<strong>urgence médicale</strong> (appeler le 15/112)</li>
              <li>Accepte que certaines pathologies ne peuvent pas être diagnostiquées à distance</li>
              <li>S'engage à ne pas enregistrer la consultation sans accord du professionnel</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mt-4">7.4 Consultations transfrontalières (UE)</h3>
            <p className="text-slate-600 dark:text-slate-300">
              Conformément à la Directive 2005/36/CE sur la reconnaissance des qualifications professionnelles 
              et à la Directive 2011/24/UE sur les soins transfrontaliers :
            </p>
            <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2">
              <li>Les diplômes médicaux sont mutuellement reconnus entre États membres de l'UE/EEE</li>
              <li>Les ordonnances délivrées sont valables dans tous les États membres</li>
              <li>Le remboursement dépend des règles du pays d'affiliation du patient</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mt-4">7.5 Exclusion de responsabilité</h3>
            <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mt-2">
              <p className="text-red-800 dark:text-red-300 font-medium">
                drbooking décline toute responsabilité pour :
              </p>
              <ul className="list-disc pl-6 text-red-700 dark:text-red-400 space-y-1 mt-2">
                <li>Les erreurs médicales commises lors des téléconsultations</li>
                <li>Les abus ou comportements inappropriés des professionnels ou patients</li>
                <li>Les conséquences de diagnostics ou traitements prescrits</li>
                <li>L'exercice illégal de la médecine par un utilisateur</li>
                <li>Les litiges entre patients et professionnels</li>
              </ul>
            </div>
          </section>

          {/* Payments */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">7. Paiements</h2>
            <p className="text-slate-600 dark:text-slate-300">
              Certains services peuvent être payants. Les paiements sont sécurisés par Stripe. 
              Les tarifs sont indiqués avant toute transaction.
            </p>
            <p className="text-slate-600 dark:text-slate-300">
              Les conditions d'annulation et de remboursement sont précisées lors de chaque réservation.
            </p>
          </section>

          {/* Liability */}
          <section className="mb-8">
            <h2 className="flex items-center gap-2 text-2xl font-bold text-slate-900 dark:text-white">
              <AlertTriangle className="h-6 w-6 text-orange-500" />
              8. Responsabilité
            </h2>
            <p className="text-slate-600 dark:text-slate-300">
              drbooking est un intermédiaire technique et ne se substitue pas aux professionnels de santé. 
              La Plateforme ne peut être tenue responsable :
            </p>
            <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2">
              <li>Des actes médicaux réalisés par les professionnels</li>
              <li>Des informations fournies par les utilisateurs</li>
              <li>Des interruptions temporaires de service</li>
              <li>Des dommages indirects liés à l'utilisation de la Plateforme</li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">9. Propriété intellectuelle</h2>
            <p className="text-slate-600 dark:text-slate-300">
              Tous les éléments de la Plateforme (logo, textes, images, code) sont protégés par le droit 
              de la propriété intellectuelle. Toute reproduction non autorisée est interdite.
            </p>
          </section>

          {/* Data Protection */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">10. Protection des données</h2>
            <p className="text-slate-600 dark:text-slate-300">
              Le traitement des données personnelles est régi par notre{' '}
              <a href="/privacy" className="text-blue-600 hover:underline">Politique de Confidentialité</a>, 
              conformément au Règlement Général sur la Protection des Données (RGPD).
            </p>
          </section>

          {/* Modifications */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">11. Modification des CGU</h2>
            <p className="text-slate-600 dark:text-slate-300">
              drbooking se réserve le droit de modifier les présentes CGU. Les utilisateurs seront 
              informés de toute modification substantielle. La poursuite de l'utilisation vaut acceptation.
            </p>
          </section>

          {/* Applicable Law */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">12. Droit applicable</h2>
            <p className="text-slate-600 dark:text-slate-300">
              Les présentes CGU sont soumises au droit luxembourgeois. Tout litige sera soumis à la 
              compétence exclusive des tribunaux du Luxembourg.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-8 p-6 bg-blue-50 dark:bg-slate-800 rounded-xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">13. Contact</h2>
            <p className="text-slate-600 dark:text-slate-300">
              Pour toute question concernant ces CGU :
            </p>
            <ul className="list-none text-slate-600 dark:text-slate-300 space-y-1 mt-2">
              <li><strong>Email</strong> : contact@drbooking.pro</li>
              <li><strong>Site</strong> : https://drbooking.pro</li>
            </ul>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
